import * as React from "react";
import { Alert, Button, notification } from "antd";
import { useLocation } from "react-router-dom";
import { acceptTask, fetchMyOrgInvitations } from "../../services/task";
import { Loader } from "../../components/layout/loader";
import { Layout } from "../../layout";
import { request } from "../../services/cpln";
import { Task } from "../../schema/types/task";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { selectInitialUrl, setInitialUrl } from "../../store/uiDataSlice";
import { handleSignOut } from "../../store/userSlice";
import { fetchOrgInvites, fetchOrgs } from "../../store/userDataSlice";
import { setOrg, setRedirect } from "../../store/contextSlice";
import { sentryLog } from "../../tools/sentry";

export const TaskRoute: React.FC = () => {
  const [isPageReady, setIsPageReady] = React.useState(false);
  const [processingTaskId, setProcessingTaskId] = React.useState<string | null>(null);
  const [tasks, setTasks] = React.useState<Task[]>([]);
  const { pathname, search } = useLocation();

  const [alreadyAccepted, setAlreadyAccepted] = React.useState(false);
  const [givenTaskId, setGivenTaskId] = React.useState("");

  const dispatch = useAppDispatch();
  const initialUrl = useAppSelector(selectInitialUrl);

  React.useEffect(() => {
    if (initialUrl.includes("/task/")) {
      dispatch(setInitialUrl(`/`));
    }

    handleNotFoundTask();
  }, []);

  React.useEffect(() => {
    let orgNameToAccept = "";
    try {
      orgNameToAccept = search.split("?complete=")[1];
    } catch (e) {
      orgNameToAccept = "";
    }

    fetchMyOrgInvitations().then((_tasks: Task[]) => {
      setTasks(_tasks);
      setIsPageReady(true);
      if (orgNameToAccept) {
        const taskToAccept = _tasks.find((task) => task.context!.orgLink === `/org/${orgNameToAccept}`);
        if (taskToAccept) {
          onAcceptTask(taskToAccept);
        }
      }
    });
  }, []);

  async function handleNotFoundTask() {
    const taskIdArr = pathname.split("/task/");
    const hasTaskId = taskIdArr.length > 1 && !!taskIdArr[1];
    if (!hasTaskId) {
      return;
    }
    const taskId = taskIdArr[1];
    try {
      const { data: task } = await request({ url: `/task/${taskId}` });
      if (task.status === "complete") {
        setGivenTaskId(taskId);
        setAlreadyAccepted(true);
      }
    } catch (e) {
      // Redirect user to a login page if the task is not found
      sentryLog(`sign out, no task found`, { "data.taskId": taskId });
      await handleSignOut(true);
    }
  }

  async function onAcceptTask(task: Task) {
    try {
      setProcessingTaskId(task.id);

      const orgLink = task.context!.orgLink;
      const orgName = orgLink.split("/")[2];

      await acceptTask(task.id);

      const newTasks = await fetchMyOrgInvitations();
      setTasks(newTasks);

      setProcessingTaskId(null);
      notification.success({
        message: "Success",
        description: `Accepted invitation to ${orgName}`,
      });

      dispatch(setInitialUrl(`/`));
      await Promise.all([dispatch(fetchOrgInvites()), dispatch(fetchOrgs())]);

      dispatch(setOrg(orgName));
      dispatch(setRedirect(`/org/${orgName}`));
    } catch (e) {
      setProcessingTaskId(null);

      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({
        message: "Failed",
        description: errorMessage,
      });
    }
  }

  if (!isPageReady) {
    return <Loader />;
  }

  return (
    <Layout showLeftMenu={false}>
      <div className="max-w-2xl">
        <h1 className="text-2xl font-semibold mb-6">Pending Invitations</h1>

        {alreadyAccepted && (
          <div className="mb-4">
            <Alert showIcon type="info" message={`Task ${givenTaskId} was already accepted`} />
          </div>
        )}

        {tasks.length === 0 ? (
          <div className="text-gray-500">No pending invitations</div>
        ) : (
          <div className="flex flex-col gap-3">
            {tasks.map((task) => {
              const isProcessing = processingTaskId === task.id;
              return (
                <div
                  key={task.id}
                  className="flex items-center justify-between p-4 border rounded-lg"
                >
                  <div className="flex-1 mr-4">{task.description}</div>
                  <Button
                    type="primary"
                    disabled={processingTaskId !== null}
                    loading={isProcessing}
                    onClick={() => onAcceptTask(task)}
                  >
                    Accept
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default TaskRoute;
